import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { formatApiErrorDetail } from '../lib/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Camera, Upload as UploadIcon, FileText, X, Loader2, ArrowRight,
  ArrowLeft, Zap, Award, Clock, ChevronRight, Check
} from 'lucide-react';

const STEPS = ['upload', 'analysis', 'pricing', 'confirm'];

export default function UploadPage() {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [docData, setDocData] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState(null);
  const [settingPriority, setSettingPriority] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(selected.type)) {
      toast.error('Only JPG, PNG, WEBP images are allowed');
      return;
    }
    if (selected.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10MB)');
      return;
    }
    setFile(selected);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(selected);
  };

  const handleUploadAndAnalyze = async () => {
    if (!file) { toast.error('Please select a document'); return; }
    if (!title.trim()) { toast.error('Please enter a title'); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title.trim());
    formData.append('description', description.trim());
    formData.append('offered_price', '0');

    try {
      const { data } = await api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 75000,
      });
      setDocData(data);
      setStep(1); // Move to analysis step
      if (data.ocr_status === 'failed') {
        toast.warning('Your file is saved, but AI transcription is unavailable. Manual transcription is still available.');
      } else {
        toast.success('Document scanned successfully!');
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleSelectPriority = async (priority) => {
    if (!docData) return;
    setSelectedPriority(priority);
    setSettingPriority(true);
    try {
      const { data } = await api.post(`/documents/${docData.id}/select-priority`, { priority });
      setDocData(prev => ({ ...prev, priority, selected_price: data.selected_price, offered_price: data.selected_price }));
      setStep(3); // Move to confirm
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSettingPriority(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const PRIORITY_OPTIONS = docData ? [
    {
      key: 'fastest',
      label: 'Fastest',
      price: docData.price_fastest,
      desc: '2-4 hours delivery',
      icon: Zap,
      color: 'var(--brand-danger)',
    },
    {
      key: 'best_quality',
      label: 'Best Quality',
      price: docData.price_best,
      desc: 'Top-rated transcriber',
      icon: Award,
      color: 'var(--brand-primary)',
    },
    {
      key: 'budget',
      label: 'Budget',
      price: docData.price_budget,
      desc: 'Slower delivery',
      icon: Clock,
      color: 'var(--brand-accent)',
    },
  ] : [];

  return (
    <div className="pb-nav" data-testid="upload-page">
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Progress indicator */}
        <div className="flex items-center gap-2 mb-6" data-testid="upload-progress">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div
                className={`w-8 h-8 flex items-center justify-center text-xs font-bold transition-colors ${
                  i <= step
                    ? 'bg-[var(--brand-primary)] text-white'
                    : 'bg-[var(--brand-border)] text-[var(--brand-text-sec)]'
                }`}
              >
                {i < step ? <Check size={14} /> : i + 1}
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 ${i < step ? 'bg-[var(--brand-primary)]' : 'bg-[var(--brand-border)]'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 0: Upload */}
        {step === 0 && (
          <div data-testid="step-upload">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[var(--brand-text)] mb-1">
              Upload Document
            </h1>
            <p className="text-sm text-[var(--brand-text-sec)] mb-6">
              Turn handwriting into text in hours
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />

            {!preview ? (
              <div className="space-y-3 mb-6">
                <button
                  data-testid="upload-document-btn"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-[var(--brand-border)] bg-white p-8 flex flex-col items-center justify-center cursor-pointer hover:border-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/5 transition-all"
                >
                  <UploadIcon size={32} className="text-[var(--brand-text-sec)] mb-3" />
                  <p className="font-medium text-[var(--brand-text)]">Upload Document</p>
                  <p className="text-sm text-[var(--brand-text-sec)]">JPG, PNG, WEBP up to 10MB</p>
                </button>
                <button
                  data-testid="take-photo-btn"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full bg-white border border-[var(--brand-border)] p-4 flex items-center gap-4 cursor-pointer hover:bg-[var(--brand-bg)] transition-colors"
                >
                  <div className="w-12 h-12 bg-[var(--brand-primary)]/10 flex items-center justify-center">
                    <Camera size={24} className="text-[var(--brand-primary)]" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-[var(--brand-text)]">Take Photo</p>
                    <p className="text-sm text-[var(--brand-text-sec)]">Use your camera to scan</p>
                  </div>
                  <ChevronRight size={18} className="text-[var(--brand-text-sec)]" />
                </button>
              </div>
            ) : (
              <div className="mb-6">
                <div className="relative bg-white border border-[var(--brand-border)]" data-testid="upload-preview">
                  <img src={preview} alt="Preview" className="w-full max-h-64 object-contain p-4" />
                  <button
                    type="button"
                    onClick={clearFile}
                    data-testid="clear-upload"
                    className="absolute top-2 right-2 w-8 h-8 bg-[var(--brand-text)] text-white flex items-center justify-center hover:bg-[var(--brand-text)]/80"
                  >
                    <X size={16} />
                  </button>
                </div>
                <div className="border border-[var(--brand-border)] border-t-0 p-3 flex items-center gap-2 bg-white">
                  <FileText size={16} className="text-[var(--brand-text-sec)]" />
                  <span className="text-sm text-[var(--brand-text-sec)] truncate">{file?.name}</span>
                </div>
              </div>
            )}

            <div className="space-y-4 mb-6">
              <div>
                <Label className="text-sm font-semibold tracking-tight text-[var(--brand-text)]">Title</Label>
                <Input
                  data-testid="upload-title-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Handwritten letter from 1920"
                  className="mt-1.5 rounded-sm border-[var(--brand-border)]"
                  required
                />
              </div>
              <div>
                <Label className="text-sm font-semibold tracking-tight text-[var(--brand-text)]">Description (optional)</Label>
                <textarea
                  data-testid="upload-description-input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Any context about the document..."
                  rows={2}
                  className="mt-1.5 w-full rounded-sm border border-[var(--brand-border)] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                />
              </div>
            </div>

            <button
              data-testid="upload-submit-button"
              onClick={handleUploadAndAnalyze}
              disabled={uploading || !file || !title.trim()}
              className="w-full bg-[var(--brand-primary)] text-white rounded-sm px-6 py-3 font-medium hover:bg-[var(--brand-primary-hover)] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {uploading ? (
                <><Loader2 size={18} className="animate-spin" />Scanning Document...</>
              ) : (
                <><UploadIcon size={18} />Upload & Scan</>
              )}
            </button>
          </div>
        )}

        {/* Step 1: AI Analysis */}
        {step === 1 && docData && (
          <div data-testid="step-analysis">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[var(--brand-text)] mb-1">
              AI Analysis
            </h1>
            <p className="text-sm text-[var(--brand-text-sec)] mb-6">Document scanned and analyzed</p>

            <div className="bg-white border border-[var(--brand-border)] divide-y divide-[var(--brand-border)] mb-6">
              <AnalysisRow label="Pages" value={docData.page_count || 1} testId="analysis-pages" />
              <AnalysisRow label="Words" value={`~${docData.word_count || 0}`} testId="analysis-words" />
              <AnalysisRow
                label="Legibility"
                value={
                  <Badge className={`rounded-sm text-[10px] ${
                    docData.legibility === 'high' ? 'bg-[var(--brand-accent)]/10 text-[var(--brand-accent)]' :
                    docData.legibility === 'medium' ? 'bg-[var(--brand-warning)]/10 text-[var(--brand-warning)]' :
                    'bg-[var(--brand-danger)]/10 text-[var(--brand-danger)]'
                  }`}>
                    {(docData.legibility || 'unknown').charAt(0).toUpperCase() + (docData.legibility || 'unknown').slice(1)}
                  </Badge>
                }
                testId="analysis-legibility"
              />
              <AnalysisRow label="Document Type" value={docData.document_type || 'Unknown'} testId="analysis-type" />
              <AnalysisRow
                label="AI Confidence"
                value={`${Math.round((docData.ocr_confidence || 0) * 100)}%`}
                testId="analysis-confidence"
              />
              <AnalysisRow
                label="Difficulty"
                value={
                  <Badge className={`rounded-sm text-[10px] ${
                    docData.difficulty === 'easy' ? 'bg-[var(--brand-accent)]/10 text-[var(--brand-accent)]' :
                    docData.difficulty === 'medium' ? 'bg-[var(--brand-warning)]/10 text-[var(--brand-warning)]' :
                    'bg-[var(--brand-danger)]/10 text-[var(--brand-danger)]'
                  }`}>
                    {(docData.difficulty || 'medium').charAt(0).toUpperCase() + (docData.difficulty || 'medium').slice(1)}
                  </Badge>
                }
                testId="analysis-difficulty"
              />
              <AnalysisRow label="Est. Time" value={`~${docData.estimated_minutes || 10} min`} testId="analysis-time" />
            </div>

            {docData.ocr_text && (
              <div className="bg-[var(--brand-bg)] border border-[var(--brand-border)] p-4 mb-6">
                <p className="text-xs font-bold tracking-[0.2em] uppercase text-[var(--brand-text-sec)] mb-2">OCR Preview</p>
                <p className="text-sm font-mono text-[var(--brand-text)] line-clamp-4">{docData.ocr_text}</p>
              </div>
            )}

            <button
              data-testid="continue-to-pricing"
              onClick={() => setStep(2)}
              className="w-full bg-[var(--brand-primary)] text-white rounded-sm px-6 py-3 font-medium hover:bg-[var(--brand-primary-hover)] transition-colors flex items-center justify-center gap-2"
            >
              Continue to Pricing <ArrowRight size={18} />
            </button>
          </div>
        )}

        {/* Step 2: Pricing */}
        {step === 2 && docData && (
          <div data-testid="step-pricing">
            <button onClick={() => setStep(1)} className="flex items-center gap-2 text-sm text-[var(--brand-text-sec)] hover:text-[var(--brand-text)] mb-4">
              <ArrowLeft size={16} /> Back
            </button>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[var(--brand-text)] mb-1">
              Choose Priority
            </h1>
            <p className="text-sm text-[var(--brand-text-sec)] mb-6">Select your preferred speed and quality</p>

            <div className="space-y-3">
              {PRIORITY_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  data-testid={`priority-${opt.key}`}
                  onClick={() => handleSelectPriority(opt.key)}
                  disabled={settingPriority}
                  className={`w-full bg-white border-2 p-5 flex items-center gap-4 transition-all hover:-translate-y-0.5 hover:shadow-md disabled:opacity-50 ${
                    selectedPriority === opt.key ? 'border-[var(--brand-primary)]' : 'border-[var(--brand-border)]'
                  }`}
                >
                  <div
                    className="w-12 h-12 flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${opt.color}15` }}
                  >
                    <opt.icon size={24} style={{ color: opt.color }} />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-semibold text-[var(--brand-text)]">{opt.label}</p>
                    <p className="text-sm text-[var(--brand-text-sec)]">{opt.desc}</p>
                  </div>
                  <span className="text-2xl font-black font-mono" style={{ color: opt.color }}>
                    ${opt.price?.toFixed(2)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && docData && (
          <div data-testid="step-confirm">
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-[var(--brand-accent)]/10 flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-[var(--brand-accent)]" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-[var(--brand-text)] mb-2">
                Document Submitted!
              </h1>
              <p className="text-sm text-[var(--brand-text-sec)] mb-6">
                Your document is now available for transcribers
              </p>

              <div className="bg-white border border-[var(--brand-border)] p-6 text-left mb-6">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-[var(--brand-text-sec)]">Title</span>
                  <span className="text-sm font-semibold text-[var(--brand-text)]">{docData.title}</span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-[var(--brand-text-sec)]">Priority</span>
                  <Badge className="rounded-sm bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]">
                    {docData.priority || 'Not set'}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[var(--brand-text-sec)]">Price</span>
                  <span className="text-xl font-black font-mono text-[var(--brand-primary)]">
                    ${(docData.selected_price || docData.offered_price || 0).toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  data-testid="view-document-btn"
                  onClick={() => navigate(`/document/${docData.id}`)}
                  className="w-full bg-[var(--brand-primary)] text-white rounded-sm px-6 py-3 font-medium hover:bg-[var(--brand-primary-hover)] transition-colors"
                >
                  Track Progress
                </button>
                <button
                  data-testid="upload-another-btn"
                  onClick={() => { setStep(0); setFile(null); setPreview(null); setTitle(''); setDescription(''); setDocData(null); }}
                  className="w-full bg-white text-[var(--brand-text)] border border-[var(--brand-border)] rounded-sm px-6 py-3 font-medium hover:bg-[var(--brand-bg)] transition-colors"
                >
                  Upload Another
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AnalysisRow({ label, value, testId }) {
  return (
    <div className="flex justify-between items-center px-6 py-4" data-testid={testId}>
      <span className="text-sm text-[var(--brand-text-sec)]">{label}</span>
      <span className="text-sm font-semibold text-[var(--brand-text)]">{value}</span>
    </div>
  );
}

